import { type FC, useEffect, useRef, useState } from 'react';
import Button from '@components/Button';
import { Show } from '@components/Show';

import { QuoteChip } from './QuoteChip';

interface CommentComposerProps {
  autoFocus?: boolean;
  disabled?: boolean;
  placeholder: string;
  quote?: string;
  submitLabel: string;
  onSubmit: (text: string) => Promise<void>;
}

/** The panel's existing textarea + primary button, shared by replies and new threads. */
export const CommentComposer: FC<CommentComposerProps> = ({
  autoFocus,
  disabled,
  placeholder,
  quote,
  submitLabel,
  onSubmit,
}) => {
  const [text, setText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const hasText = Boolean(text.trim());
  const isBusy = Boolean(disabled) || isSubmitting;

  useEffect(() => {
    if (autoFocus) {
      textareaRef.current?.focus({ preventScroll: true });
    }
  }, [autoFocus]);

  const handleSubmit = async () => {
    if (!hasText || isBusy) {
      return;
    }
    setIsSubmitting(true);
    try {
      await onSubmit(text.trim());
      setText('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Show when={Boolean(quote)}>
        <QuoteChip isRemoved={false} quote={quote ?? ''} testId="comment-composer-quote" />
      </Show>
      <div className="px-7 py-5">
        <div className="mb-[18px] flex rounded-md bg-white">
          <textarea
            className="w-full rounded-md border-2 p-3 text-xs leading-[18px] shadow-sm focus:border-blue-600"
            data-testid="comment-textarea"
            disabled={isBusy}
            id="add-comment-input"
            maxLength={2000}
            onChange={(event) => setText(event.target.value)}
            placeholder={placeholder}
            ref={textareaRef}
            rows={3}
            value={text}
          />
        </div>
        <div className="flex justify-end gap-3">
          <Button
            className="h-[34px] px-3 py-2 text-sm"
            data-testid="comment-submit-button"
            isDisabled={!hasText || isBusy}
            kind="primary"
            loading={isBusy}
            onClick={handleSubmit}
            size="sm"
          >
            {submitLabel}
          </Button>
        </div>
      </div>
    </>
  );
};
